import React from "react";
import {
  activeFilterCount,
  isSameFilterValues,
  normalizeFilterValues,
  type FilterSchema,
  type FilterValues,
} from "./filter-schema.js";

/* ---------------- useFilterMenuController (PB-2941) ----------------
   The one controller behind the FilterMenu trigger/panel AND the applied chip
   rail, so a consuming module never hand-writes draft state, apply/cancel
   plumbing, or remove-one handlers. APPLIED state stays controlled by the
   consumer (it typically lives in the URL — see useFilterUrlState); the
   controller owns the open state and the DRAFT, which only becomes visible to
   results on Apply. */

export interface FilterMenuController<Row = unknown> {
  readonly schema: FilterSchema<Row>;
  /** Normalized applied values (what results currently reflect). */
  readonly applied: FilterValues;
  /** Fields with ≥1 applied value — shown on the trigger. */
  readonly activeCount: number;
  readonly open: boolean;
  /** Opening seeds the draft from the applied values; closing discards it. */
  readonly setOpen: (open: boolean) => void;
  readonly draft: FilterValues;
  readonly setDraftValues: (fieldId: string, values: readonly string[]) => void;
  /** Commit the draft. Emits ONE change — and none when nothing changed. */
  readonly apply: () => void;
  /** Discard the draft and close. */
  readonly cancel: () => void;
  /** Empty the draft (Apply then commits the cleared state). */
  readonly clearDraft: () => void;
  /** Remove one applied value (or a whole field) — chip remove. */
  readonly removeApplied: (fieldId: string, value?: string) => void;
  /** Clear every applied value — the chip rail's "Clear all". */
  readonly clearApplied: () => void;
}

export interface UseFilterMenuControllerOptions<Row> {
  readonly schema: FilterSchema<Row>;
  readonly applied: FilterValues;
  readonly onAppliedChange: (next: FilterValues) => void;
}

export function useFilterMenuController<Row = unknown>({
  schema,
  applied: appliedInput,
  onAppliedChange,
}: UseFilterMenuControllerOptions<Row>): FilterMenuController<Row> {
  const applied = React.useMemo(
    () => normalizeFilterValues(schema, appliedInput),
    [schema, appliedInput],
  );
  const [open, setOpenState] = React.useState(false);
  const [draft, setDraft] = React.useState<FilterValues>(applied);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (next) setDraft(applied);
      setOpenState(next);
    },
    [applied],
  );

  const setDraftValues = React.useCallback(
    (fieldId: string, values: readonly string[]) => {
      setDraft((current) => ({ ...current, [fieldId]: values }));
    },
    [],
  );

  const commit = React.useCallback(
    (next: FilterValues) => {
      if (!isSameFilterValues(next, applied)) onAppliedChange(next);
    },
    [applied, onAppliedChange],
  );

  const apply = React.useCallback(() => {
    commit(normalizeFilterValues(schema, draft));
    setOpenState(false);
  }, [commit, draft, schema]);

  const cancel = React.useCallback(() => setOpenState(false), []);

  const clearDraft = React.useCallback(() => setDraft({}), []);

  const removeApplied = React.useCallback(
    (fieldId: string, value?: string) => {
      const next: Record<string, readonly string[]> = { ...applied };
      if (value === undefined) {
        delete next[fieldId];
      } else {
        const remaining = (next[fieldId] ?? []).filter((v) => v !== value);
        if (remaining.length > 0) next[fieldId] = remaining;
        else delete next[fieldId];
      }
      commit(normalizeFilterValues(schema, next));
    },
    [applied, commit, schema],
  );

  const clearApplied = React.useCallback(() => commit({}), [commit]);

  return {
    schema,
    applied,
    activeCount: activeFilterCount(schema, applied),
    open,
    setOpen,
    draft,
    setDraftValues,
    apply,
    cancel,
    clearDraft,
    removeApplied,
    clearApplied,
  };
}
